import type { SocialMediaPostStatusData } from "@/types/jobs";
import { ref, onUnmounted } from "vue";

interface PostStatusUpdate {
  status: "processing" | "completed" | "error";
  error?: string;
  postUrl?: string;
}

type PostStatusCallback = (update: PostStatusUpdate) => void;

const activePostListeners = ref<
  Map<string, { callbacks: Set<PostStatusCallback>; timeout?: number }>
>(new Map());

const LISTENER_TIMEOUT = 5 * 60 * 1000; // 5 minutes

/**
 * Composable to handle social media post status updates via websockets
 */
export function useSocialMediaPostStatus(contentId: string) {
  const statusCallbacks = new Set<PostStatusCallback>();

  const subscribe = (callback: PostStatusCallback) => {
    statusCallbacks.add(callback);

    // Register this content ID if not already registered
    if (!activePostListeners.value.has(contentId)) {
      activePostListeners.value.set(contentId, {
        callbacks: new Set(),
      });
    }

    const listener = activePostListeners.value.get(contentId);
    if (listener) {
      listener.callbacks.add(callback);

      // Clear existing timeout
      if (listener.timeout) {
        clearTimeout(listener.timeout);
      }

      // Set new timeout to clean up listener after 5 minutes
      listener.timeout = window.setTimeout(() => {
        activePostListeners.value.delete(contentId);
      }, LISTENER_TIMEOUT);
    }
  };

  const unsubscribe = (callback: PostStatusCallback) => {
    statusCallbacks.delete(callback);

    const listener = activePostListeners.value.get(contentId);
    if (listener) {
      listener.callbacks.delete(callback);
      if (listener.callbacks.size === 0) {
        if (listener.timeout) {
          clearTimeout(listener.timeout);
        }
        activePostListeners.value.delete(contentId);
      }
    }
  };

  onUnmounted(() => {
    statusCallbacks.forEach(callback => unsubscribe(callback));
  });

  return {
    subscribe,
    unsubscribe,
  };
}

/**
 * Global function to handle incoming websocket messages for social media post status
 * This should be called from the main websocket handler
 */
export function handleSocialMediaPostStatus(data: SocialMediaPostStatusData) {
  const { contentId, status, error, postUrl } = data;

  const listener = activePostListeners.value.get(contentId);
  if (!listener) {
    console.warn(
      `[socialMediaPostWebsocket] No listener found for contentId: ${contentId}. Active listeners:`,
      Array.from(activePostListeners.value.keys())
    );
    return;
  }

  const update: PostStatusUpdate = {
    status,
    error,
    postUrl,
  };

  // Notify all callbacks for this content ID
  listener.callbacks.forEach(callback => {
    try {
      callback(update);
    } catch (err) {
      console.error(
        "[socialMediaPostWebsocket] Error in social media post status callback:",
        err
      );
    }
  });

  // Clean up listener if status is completed or error
  if (status === "completed" || status === "error") {
    if (listener.timeout) {
      clearTimeout(listener.timeout);
    }
    // Delay removal to allow UI to update
    setTimeout(() => {
      activePostListeners.value.delete(contentId);
    }, 1000);
  }
}
